import { LinkObject } from "../../managers";

export type { LinkObject } from "../../managers/mail/types/mail";

/**
 * Signature de la fonction extractLinks partagée entre les implémentations Web et React Native.
 */
export type ExtractLinksFunction = (htmlInput: string) => LinkObject[];

/**
 * Placeholder utilisé pour neutraliser les références cid: dans le HTML.
 */
export const CID_PLACEHOLDER =
    'src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"';

/**
 * Regex pour détecter les attributs src avec protocole cid:.
 */
export const CID_REGEX = /src=["']cid:[^"']+["']/gi;

/**
 * Nettoie une chaîne de texte en supprimant les caractères d'échappement et les espaces superflus.
 * Utilisé par les deux implémentations (Web DOM et React Native regex).
 */
export function _cleanText(text: string): string {
    return text
        .replace(/\\r\\n/g, " ")
        .replace(/\\n/g, " ")
        .replace(/\\r/g, " ")
        .replace(/\\\"/g, "")
        .replace(/\\\'/g, "")
        .replace(/\\\\/g, "")
        .replace(/\\/g, "")
        .replace(/\s+/g, " ")
        .trim();
}

/**
 * Paramètres de query réputés « tracking » : ils n'affectent pas la destination réelle du lien.
 * Retirés uniquement de la clé de déduplication (jamais de l'URL retournée).
 * En plus de cette liste, tout paramètre dont le nom (lowercase) commence par "utm_" est retiré.
 */
const TRACKING_PARAM_EXACT: ReadonlySet<string> = new Set([
    "gclid",
    "gclsrc",
    "dclid",
    "fbclid",
    "msclkid",
    "mc_eid",
    "mc_cid",
    "_hsenc",
    "_hsmi",
    "vero_id",
    "vero_conv",
    "igshid",
    "oly_enc_id",
    "oly_anon_id",
    "yclid",
    "wickedid",
    "ref_src",
    "tracking_data",
]);

/** Profondeur maximale de déballage des redirections (garde-fou anti-boucle infinie). */
const MAX_REDIRECT_DEPTH = 10;

/**
 * Domaines Google enregistrables autorisés pour le redirect `/url?q=`. Tous les services Google
 * (Agenda, Docs, Search — consolidé sur google.com depuis ~2018) émettent le wrapper sur
 * `www.google.com/url`, d'où la restriction à `google.com`. Supporter les ccTLD imposerait une
 * allowlist de suffixes (sinon `google.evil.com` passerait, cf. « public suffix ») pour un gain quasi
 * nul. Fail-safe : un éventuel `google.fr/url` legacy n'est pas déballé (jamais une faille).
 * Étendre = ajouter une entrée ici.
 */
const GOOGLE_REDIRECT_DOMAINS: ReadonlySet<string> = new Set(["google.com"]);

/**
 * Vrai ssi `host` EST un domaine Google autorisé ou en est un sous-domaine (`*.google.<tld>`).
 * Jamais l'inverse : `google.evil.com` (google = sous-domaine d'un attaquant) est rejeté.
 */
function _isGoogleRedirectHost(host: string): boolean {
    for (const domain of GOOGLE_REDIRECT_DOMAINS) {
        if (host === domain || host.endsWith(`.${domain}`)) {
            return true;
        }
    }
    return false;
}

/**
 * Déballe UN niveau de wrapper de redirection (Safelinks ou Hornet), SANS décoder le résultat.
 * Retourne null si `url` n'est pas un wrapper reconnu (ou ne porte pas de cible).
 *
 * Détection par hostname RÉEL (via `URL`) et non par regex sur la chaîne brute : une cible imbriquée
 * peut contenir le domaine d'un autre wrapper dans sa query — seul le host parsé identifie le wrapper.
 * Ne PAS décoder ici est essentiel pour l'imbriqué : `searchParams.get` décode exactement une fois
 * (le niveau courant), et une cible peut être elle-même un wrapper dont les paramètres sont encore encodés.
 */
function _unwrapRedirectOnce(url: string): string | null {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }

    const host = parsed.hostname.toLowerCase();
    if (
        host === "safelinks.protection.outlook.com" ||
        host.endsWith(".safelinks.protection.outlook.com")
    ) {
        return parsed.searchParams.get("url") || null;
    }
    if (host === "atpscan.global.hornetsecurity.com") {
        return parsed.searchParams.get("u") || null;
    }
    // Google redirect (Agenda, Docs, Search…) : https://www.google.com/url?q=<cible>.
    // On EXIGE le path '/url' pour ne pas confondre avec une recherche google.com/search?q=<texte>.
    if (parsed.pathname === "/url" && _isGoogleRedirectHost(host)) {
        return parsed.searchParams.get("q") || null;
    }
    return null;
}

/**
 * Résout récursivement les wrappers de redirection imbriqués (Safelinks/Hornet) jusqu'à la cible
 * finale. S'arrête dès qu'aucun wrapper n'est reconnu, sur cycle (`next === current`), ou à
 * MAX_REDIRECT_DEPTH. Ne décode pas (voir `_unwrapRedirectOnce`).
 */
export function resolveRedirectTarget(url: string): string {
    let current = url;
    for (let depth = 0; depth < MAX_REDIRECT_DEPTH; depth++) {
        const next = _unwrapRedirectOnce(current);
        if (next === null || next === current) {
            break;
        }
        current = next;
    }
    return current;
}

/**
 * Extrait l'URL cible *affichable* d'une URL, en gérant les redirections Safelinks/Hornet
 * **imbriquées** (déballage récursif), puis en décodant une fois pour la lisibilité.
 *
 * Utilisée comme nom d'affichage de repli quand un `<a>` n'a ni texte ni alt d'image : on montre
 * alors la destination réelle plutôt que le wrapper. PARTAGÉE avec le backend (cf. `baseMail.utils.ts`
 * `_extractTargetUrl`, mêmes entrées → même sortie).
 */
export function _extractTargetUrl(url: string): string {
    const target = resolveRedirectTarget(url);
    try {
        return decodeURI(target);
    } catch {
        return target;
    }
}

/**
 * Calcule une clé de déduplication normalisée pour une URL.
 *
 * La clé sert UNIQUEMENT à comparer deux liens entre eux : l'URL d'origine reste conservée telle
 * quelle dans le LinkObject retourné par extractLinks. Règles (PARTAGÉES avec le backend —
 * cf. `baseMail.utils.ts` `computeDedupKey`, mêmes entrées → même clé) :
 *  1. déballage récursif des wrappers de redirection (Safelinks/Hornet) → cible finale ;
 *  2. normalisation via `URL` : lowercase scheme + host, retrait du fragment `#...`, retrait du
 *     slash final du path ;
 *  3. retrait des paramètres de tracking (`utm_*`, `gclid`, ...) puis tri des restants (dédup
 *     insensible à l'ordre).
 *
 * Les URLs non http(s) (mailto:, tel:, relatives...) sont renvoyées quasi telles quelles.
 *
 * ⚠️ Dépend de `URL`/`URLSearchParams` : natif sur web, requiert un polyfill sur React Native
 * (l'app mobile fournit `react-native-url-polyfill`).
 * Entrée attendue : une url déjà passée par `_cleanText`.
 */
export function computeDedupKey(url: string): string {
    const target = resolveRedirectTarget(url.trim());
    if (!target) {
        return url;
    }

    try {
        const parsed = new URL(target);

        // Schemes non http(s) (mailto:, tel:, ...) : pas de normalisation query/host/path
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            return target;
        }

        // Retrait des paramètres de tracking, puis tri des restants
        const params = new URLSearchParams();
        for (const [name, value] of parsed.searchParams.entries()) {
            const lowerName = name.toLowerCase();
            if (
                TRACKING_PARAM_EXACT.has(lowerName) ||
                lowerName.startsWith("utm_")
            ) {
                continue;
            }
            params.append(name, value);
        }
        params.sort();
        const query = params.toString();

        // scheme + host déjà en lowercase via URL ; le fragment est ignoré ; slash final retiré du path
        let path = parsed.pathname;
        while (path.endsWith("/")) {
            path = path.slice(0, -1);
        }
        const querySuffix = query ? `?${query}` : "";
        const key = `${parsed.protocol}//${parsed.host}${path}${querySuffix}`;

        return key || url;
    } catch {
        // URL non absolue (relative, ...), OU API URL/URLSearchParams absente ou partielle
        // (React Native sans polyfill complet) : dégradation propre → dédup sur la cible brute.
        return target;
    }
}
